/* eslint-disable react-hooks/exhaustive-deps */
/**
 * InstallOrb — the living visual soul of Soulumo.
 *
 * A state-driven, dimensional champagne-gold sphere with a
 * luminous inner atmosphere. Designed as a signature that follows
 * the user through the whole Soulumo experience (and eventually
 * throughout Reclaim).
 *
 * States (Michael, Feb 2026):
 *   arriving      — cinematic first appearance (point of light →
 *                   particles gather → orb crystallises → halo → breath)
 *   resting       — the default: slow inner breath + fine drift
 *   listening     — soft ripples across the outer sheen
 *   interpreting  — inner threads accelerate + orbit the core
 *   building      — small golden fragments stream in from outside
 *   recording     — the orb pulses to the user's real voice (via
 *                   the optional `analyserNode` prop from getUserMedia)
 *   complete      — one warm halo wave is released, orb brightens
 *   playing       — slow expanding frequency rings
 *
 * All animation is CSS/SVG/framer-motion. No canvas, no images.
 * Refined easing, calm cadence — never frantic.
 */
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// ── Per-state animation config ────────────────────────────────────
const STATES = {
  arriving:     { spin: 60, corePulse: [1, 1.04, 1],   coreDur: 3.8, halo: 0.55, sheenOpacity: 0.75, extraBrightness: 0 },
  resting:      { spin: 50, corePulse: [1, 1.02, 1],   coreDur: 5.6, halo: 0.55, sheenOpacity: 0.85, extraBrightness: 0 },
  listening:    { spin: 40, corePulse: [1, 1.03, 1],   coreDur: 3.2, halo: 0.7,  sheenOpacity: 0.92, extraBrightness: 0.05 },
  interpreting: { spin: 22, corePulse: [1, 1.04, 1],   coreDur: 2.4, halo: 0.85, sheenOpacity: 1.00, extraBrightness: 0.12 },
  // "curating" — the middle act during creation: fragments orbit
  // the core, deliberate + slow. Distinct from interpreting's inward
  // pull and installing's outward crescendo.
  curating:     { spin: 30, corePulse: [1, 1.035, 1],  coreDur: 3.2, halo: 0.82, sheenOpacity: 0.98, extraBrightness: 0.09 },
  // "installing" — final act: orb brightens, halo expands, elements
  // converge into one luminous whole. (Alias: building — kept for
  // backwards compat.)
  installing:   { spin: 24, corePulse: [1, 1.06, 1],   coreDur: 3.0, halo: 1.05, sheenOpacity: 1.00, extraBrightness: 0.16 },
  building:     { spin: 26, corePulse: [1, 1.05, 1],   coreDur: 2.8, halo: 0.8,  sheenOpacity: 0.98, extraBrightness: 0.10 },
  recording:    { spin: 46, corePulse: [1, 1.02, 1],   coreDur: 5.6, halo: 0.65, sheenOpacity: 0.88, extraBrightness: 0.06 },
  complete:     { spin: 40, corePulse: [1, 1.06, 1],   coreDur: 4.4, halo: 1.15, sheenOpacity: 1.00, extraBrightness: 0.18 },
  playing:      { spin: 34, corePulse: [1, 1.03, 1],   coreDur: 6.4, halo: 0.85, sheenOpacity: 0.95, extraBrightness: 0.08 },
};

export default function InstallOrb({
  state = "resting",
  size = 240,
  intensity = 1,
  className = "",
  analyserNode = null,       // optional Web Audio AnalyserNode for recording state
  arrivalKey = 0,            // change this to re-trigger Arrival
}) {
  const settings = STATES[state] || STATES.resting;
  const [scale, setScale] = useState(1);
  const rafRef = useRef(null);

  // ── Voice-reactive scale for the "recording" state ─────────────
  useEffect(() => {
    if (state !== "recording" || !analyserNode) return;
    const buf = new Uint8Array(analyserNode.frequencyBinCount);
    const tick = () => {
      analyserNode.getByteFrequencyData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i += 1) sum += buf[i];
      const avg = sum / buf.length / 255; // 0..1
      // Map to a calm 1.00 → 1.06 range so it never feels jittery.
      const target = 1 + Math.min(0.06, avg * 0.14);
      setScale((s) => s + (target - s) * 0.25);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setScale(1);
    };
  }, [state, analyserNode]);

  return (
    <div className={`relative shrink-0 ${className}`} style={{ width: size, height: size }} aria-hidden>
      {/* ── Cinematic Arrival — particles gathering into a point of light ── */}
      <AnimatePresence>
        {state === "arriving" && <ArrivalStage key={arrivalKey} size={size} intensity={intensity} />}
      </AnimatePresence>

      {/* ── Outer halo — violet+amber atmosphere ── */}
      <motion.div
        className="absolute inset-0 rounded-full pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{
          opacity: settings.halo * intensity,
          scale: state === "complete" ? [0.98, 1.14, 1.02] : [0.98, 1.04, 0.98],
        }}
        transition={{
          opacity: { duration: 1.2, ease: "easeOut" },
          scale: state === "complete"
            ? { duration: 2.4, ease: "easeOut" }
            : { duration: 6.2, ease: "easeInOut", repeat: Infinity },
        }}
        style={{
          filter: `blur(${size * 0.14}px)`,
          background:
            "radial-gradient(circle at 40% 40%, rgba(245,224,173,0.55) 0%, rgba(215,153,110,0.35) 30%, rgba(140,90,180,0.30) 55%, rgba(30,50,90,0.10) 78%, transparent 100%)",
        }}
      />

      {/* ── Counter-rotating chromatic sheen ── */}
      <motion.div
        className="absolute inset-0 rounded-full pointer-events-none"
        animate={{ rotate: 360 }}
        transition={{ duration: settings.spin, ease: "linear", repeat: Infinity }}
        style={{
          background:
            "conic-gradient(from 90deg, rgba(245,224,173,0.35), rgba(255,182,193,0.22), rgba(140,90,180,0.30), rgba(90,160,230,0.22), rgba(245,224,173,0.35))",
          mixBlendMode: "screen",
          filter: `blur(${size * 0.03}px)`,
          opacity: settings.sheenOpacity,
        }}
      />

      {/* ── Inner threads — visible during interpreting/building ── */}
      {(state === "interpreting" || state === "building" || state === "complete") && (
        <InnerThreads size={size} />
      )}

      {/* ── Incoming fragments — streaming INTO the orb (building) ── */}
      {state === "building" && <IncomingFragments size={size} />}

      {/* ── Core sphere ── */}
      <motion.div
        className="absolute inset-[12%] rounded-full"
        animate={{
          scale: state === "recording"
            ? scale
            : (settings.corePulse.length > 1 ? settings.corePulse : 1),
          filter: `brightness(${1 + settings.extraBrightness})`,
        }}
        transition={{
          scale: state === "recording"
            ? { duration: 0.08, ease: "linear" }
            : { duration: settings.coreDur, ease: "easeInOut", repeat: Infinity },
          filter: { duration: 1.2, ease: "easeOut" },
        }}
        style={{
          background:
            "radial-gradient(circle at 32% 30%, #fff7dc 0%, #f5e0ad 22%, #e7c98a 45%, #b78b52 72%, #4a3466 100%)",
          boxShadow:
            "inset 0 -18px 40px rgba(20,10,40,0.55), inset 0 12px 30px rgba(255,240,200,0.28), 0 0 60px rgba(231,201,138,0.35)",
        }}
      />

      {/* ── Glass highlight cap ── */}
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          top: `${size * 0.18}px`,
          left: `${size * 0.24}px`,
          width: `${size * 0.28}px`,
          height: `${size * 0.16}px`,
          background: "radial-gradient(ellipse, rgba(255,255,255,0.72), transparent 70%)",
          filter: "blur(4px)",
          opacity: 0.9,
        }}
      />

      {/* ── Listening ripples ── */}
      {state === "listening" && <ListeningRipples size={size} />}

      {/* ── Completion halo wave ── */}
      {state === "complete" && <CompletionWave size={size} />}

      {/* ── Playback frequency rings ── */}
      {state === "playing" && <FrequencyRings size={size} />}

      {/* ── Ambient twinkles (always) ── */}
      <Twinkle top="12%"  left="70%" delay={0.4} size={size * 0.03} />
      <Twinkle top="72%"  left="18%" delay={1.7} size={size * 0.025} />
      <Twinkle top="46%"  left="86%" delay={3.1} size={size * 0.02} />
    </div>
  );
}

// ── Inner threads — slow orbiting light-strands inside the orb ─────
function InnerThreads({ size }) {
  return (
    <div className="absolute inset-[16%] rounded-full overflow-hidden pointer-events-none">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute inset-0 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 14 + i * 6, ease: "linear", repeat: Infinity, delay: i * 0.4 }}
          style={{
            background:
              i === 0
                ? "conic-gradient(from 0deg, transparent 60%, rgba(255,240,200,0.55) 72%, transparent 80%)"
                : i === 1
                ? "conic-gradient(from 120deg, transparent 55%, rgba(245,224,173,0.35) 68%, transparent 78%)"
                : "conic-gradient(from 240deg, transparent 62%, rgba(255,182,193,0.28) 74%, transparent 82%)",
            filter: `blur(${size * 0.012}px)`,
            mixBlendMode: "screen",
          }}
        />
      ))}
    </div>
  );
}

// ── Small twinkling star ───────────────────────────────────────────
function Twinkle({ top, left, size, delay = 0 }) {
  return (
    <motion.div
      className="absolute rounded-full pointer-events-none"
      style={{
        top, left, width: size, height: size,
        background: "radial-gradient(circle, #fff8d6 0%, rgba(255,248,214,0.6) 40%, transparent 80%)",
        boxShadow: "0 0 6px rgba(255,240,200,0.8)",
      }}
      initial={{ opacity: 0, scale: 0.4 }}
      animate={{ opacity: [0, 1, 0], scale: [0.4, 1, 0.4] }}
      transition={{ duration: 2.8, ease: "easeInOut", repeat: Infinity, delay }}
    />
  );
}

// ── Cinematic Arrival: gathering particles + point-of-light → orb ──
function ArrivalStage({ size, intensity }) {
  const PARTICLE_COUNT = 14;
  // Precompute deterministic start positions so re-renders don't jitter.
  const seeds = React.useMemo(
    () => Array.from({ length: PARTICLE_COUNT }, (_, i) => {
      const angle = (i / PARTICLE_COUNT) * Math.PI * 2 + (i * 0.37);
      const dist = 0.9 + ((i * 13) % 30) / 100; // 0.9..1.2 of half-size
      return {
        x: Math.cos(angle) * dist * (size / 2),
        y: Math.sin(angle) * dist * (size / 2),
        delay: (i % 7) * 0.08,
      };
    }),
    [size],
  );

  return (
    <>
      {/* Faint point-of-light seed */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          top: "50%", left: "50%", width: 6, height: 6,
          x: -3, y: -3,
          background: "#fff8d6",
          boxShadow: "0 0 30px rgba(255,240,200,0.9), 0 0 12px rgba(255,240,200,0.8)",
        }}
        initial={{ opacity: 0, scale: 0.4 }}
        animate={{ opacity: [0, 1, 1, 0], scale: [0.4, 1, 2.6, 4] }}
        transition={{ duration: 2.1, times: [0, 0.25, 0.75, 1], ease: "easeOut" }}
      />
      {/* Gathering particles — travel INWARD to centre */}
      {seeds.map((s, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            top: "50%", left: "50%", width: 3, height: 3, marginLeft: -1.5, marginTop: -1.5,
            background: "#f5e0ad",
            boxShadow: "0 0 6px rgba(245,224,173,0.9)",
          }}
          initial={{ x: s.x, y: s.y, opacity: 0 }}
          animate={{ x: 0, y: 0, opacity: [0, 1 * intensity, 0] }}
          transition={{ duration: 1.4, ease: "easeIn", delay: s.delay }}
        />
      ))}
      {/* The one soft breath — a ring released after formation */}
      <motion.div
        className="absolute inset-0 rounded-full pointer-events-none"
        initial={{ opacity: 0, scale: 0.65 }}
        animate={{ opacity: [0, 0.55, 0], scale: [0.65, 1.3, 1.55] }}
        transition={{ duration: 1.6, delay: 1.8, ease: "easeOut" }}
        style={{
          border: "1px solid rgba(245,224,173,0.55)",
          boxShadow: "0 0 40px rgba(245,224,173,0.35)",
        }}
      />
    </>
  );
}

// ── Building: fragments flow INTO the orb from beyond ──────────────
function IncomingFragments({ size }) {
  const N = 6;
  const seeds = React.useMemo(
    () => Array.from({ length: N }, (_, i) => {
      const angle = (i / N) * Math.PI * 2 + i * 0.7;
      return {
        fromX: Math.cos(angle) * size * 0.9,
        fromY: Math.sin(angle) * size * 0.9,
        delay: i * 0.35,
      };
    }),
    [size],
  );
  return (
    <>
      {seeds.map((s, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            top: "50%", left: "50%", width: 4, height: 4, marginLeft: -2, marginTop: -2,
            background: "#fff8d6",
            boxShadow: "0 0 8px rgba(245,224,173,0.85)",
          }}
          initial={{ x: s.fromX, y: s.fromY, opacity: 0 }}
          animate={{ x: 0, y: 0, opacity: [0, 1, 0] }}
          transition={{ duration: 1.8, ease: "easeInOut", repeat: Infinity, delay: s.delay }}
        />
      ))}
    </>
  );
}

// ── Listening ripples ──────────────────────────────────────────────
function ListeningRipples({ size }) {
  return (
    <>
      {[0, 1].map((i) => (
        <motion.div
          key={i}
          className="absolute inset-0 rounded-full pointer-events-none"
          initial={{ opacity: 0, scale: 1 }}
          animate={{ opacity: [0, 0.4, 0], scale: [1, 1.18, 1.3] }}
          transition={{ duration: 2.4, ease: "easeOut", repeat: Infinity, delay: i * 1.1 }}
          style={{ border: "1px solid rgba(245,224,173,0.55)" }}
        />
      ))}
    </>
  );
}

// ── One-shot completion halo ───────────────────────────────────────
function CompletionWave({ size }) {
  return (
    <motion.div
      className="absolute inset-0 rounded-full pointer-events-none"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: [0, 0.85, 0], scale: [0.9, 1.8, 2.4] }}
      transition={{ duration: 1.8, ease: "easeOut" }}
      style={{
        border: "1px solid rgba(245,224,173,0.75)",
        boxShadow: "0 0 60px rgba(245,224,173,0.55)",
      }}
    />
  );
}

// ── Slow expanding frequency rings for Playback ────────────────────
function FrequencyRings() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute inset-0 rounded-full pointer-events-none"
          initial={{ opacity: 0, scale: 1 }}
          animate={{ opacity: [0, 0.45, 0], scale: [1, 1.35, 1.55] }}
          transition={{ duration: 4.8, ease: "easeOut", repeat: Infinity, delay: i * 1.6 }}
          style={{ border: "1px solid rgba(245,224,173,0.55)" }}
        />
      ))}
    </>
  );
}
